import { VoyageDialogPopupManager } from "../../local_modules/voyage-dialog-popup/src/voyage-dialog-popup-manager";
import { NotificationTypes } from "../services/types/vessel-notifications-services.types";
import { VesselNotificationsService } from "../services/vessel-notifications-service";
import { GetFormattedDateDayMonthTimeNoYear } from "../utils/helpers";
import { ImminentRiskModelBase, ImminentRiskType } from "./imminent-risk-model";
import audioAlert from "../../assets/audio/vessel-alert.mp3"

export enum ImminentRiskEvents {
    hasImminentRisk = "has-imminent-risk",
    hasInprogressImminentRisk = "has-inprogress-imminent-risk",
    onboardAudioAlert = "onboard-audio-alert",
    UserFlowEnd = "imminent-risk-user-flow-end"
}

export enum ImminentRiskStates {
    Active = "Active",
    InProgress = "In-progress",
    Ended = "Ended"
}
export class ImminentRiskModelImpl extends ImminentRiskModelBase {
    private _shownImminentRiskPopup = false;
    currentImminentRiskAlert: ImminentRiskType | null = null;
    private _audio?: HTMLAudioElement;
    constructor() {
        super();
        this.fetchImminentRisk();
        setInterval(() => { this.fetchImminentRisk()}, 2 * 60 * 1000);
    }

    private async fetchImminentRisk() {
        if (this._shownImminentRiskPopup) return;
        // Fetch the imminent risk data from the NAPI
        const latestAlert = await this.getLatestImminentRiskAlert();

        if(!latestAlert) {
            this._shownImminentRiskPopup = false;
            localStorage.removeItem('imminentRiskLogId');
            this.dispatchEvent(
                new CustomEvent(ImminentRiskEvents.UserFlowEnd)
            );
            this.currentImminentRiskAlert = null;
            return;
        }

        this.currentImminentRiskAlert = latestAlert;

        const log_id = latestAlert?.data.log_id;
        localStorage.setItem('imminentRiskLogId', log_id ? log_id : '');

        if(latestAlert && latestAlert.state === ImminentRiskStates.InProgress) {
            this.dispatchEvent(
                    new CustomEvent(ImminentRiskEvents.hasInprogressImminentRisk, {
                        detail: { log_id },
                        bubbles: true,
                        composed: true,
                    })
                );
        }

        if(latestAlert && latestAlert.state === ImminentRiskStates.Active) {
            this._shownImminentRiskPopup = true;
            if (!this._audio) {
                this._audio = new Audio(audioAlert);
                this._audio.loop = true; 
            }

            try {
                await this._audio.play();
            } catch (err) {
                console.warn('Unable to play imminent risk audio alert:', err);
            }

            const dialog = VoyageDialogPopupManager.createDialog({
                headerText: `High Roll Angle Risk projected within the next 30 minutes | ${GetFormattedDateDayMonthTimeNoYear(new Date().toISOString())} UTC`,
                bodyText: `Your vessel is currently at risk of Parametric and Synchronous Roll. Please view 
                the Polar Chart advice to take an immediate action.`,
                acceptButtonText: 'View Polar Chart Advice',
                noCloseButton: true,
                noHeaderBox: true,
            });

            const response = await dialog.getResponse();    
            if(response === 'accept'){
                if (this._audio) {
                    this._audio.pause();
                    this._audio.currentTime = 0; 
                }

                this.dispatchEvent(
                    new CustomEvent(ImminentRiskEvents.hasImminentRisk, {
                        detail: { log_id },
                        bubbles: true,
                        composed: true,
                    })
                );
                this.acknowledgeImminentRiskAlert(latestAlert.id, ImminentRiskStates.InProgress);
            }
        }
    }

    async acknowledgeImminentRiskAlert(alertId: string, state: string): Promise<void> {
        const latestAlert = await VesselNotificationsService.fetchAlert(alertId);
        const isEnded = latestAlert.state === ImminentRiskStates.Ended;
        const isSameState = latestAlert.state === state;
        if(!isEnded && !isSameState) {
            await VesselNotificationsService.acknowledgeImminentRiskAlert(alertId, state);
        }
    }

    async getCurrentImminentRiskAlert(): Promise<ImminentRiskType | null> {
        return this.currentImminentRiskAlert;
    }

    async getLatestImminentRiskAlert(): Promise<ImminentRiskType | null> {
        const [activeAlerts, inProgressAlerts] = await Promise.all([
            VesselNotificationsService.fetchNotificationsByTypeAndState(NotificationTypes.ImminentRisk, ImminentRiskStates.Active),
            VesselNotificationsService.fetchNotificationsByTypeAndState(NotificationTypes.ImminentRisk, ImminentRiskStates.InProgress)
        ]);

        // Combine both alerts
        const combinedAlerts = [...activeAlerts, ...inProgressAlerts];

        combinedAlerts.sort((a, b) => new Date(b.updatedAt ?? '').getTime() - new Date(a.updatedAt ?? '').getTime()); 

        const alert = combinedAlerts[0];
        return alert;
    }

    async fetchImminentRiskOnboard(): Promise<boolean> {
        // Fetch the imminent risk onboard from the NAPI
        const alerts = await VesselNotificationsService.fetchActiveNotifications(NotificationTypes.ImminentRiskOnboard);

        if (!alerts || alerts.length === 0) {
            return true;  
        }

        return false;
    }

    async finishOnboarding(audioHeard: boolean): Promise<void> {
        await VesselNotificationsService.createImminentRiskOnboardNotification(audioHeard);
    }

    async finishTesting(audioHeard: boolean): Promise<void> {
        if(!this.currentImminentRiskAlert) {
            return;
        }
        
        await VesselNotificationsService.postImminentRiskAudioAlerts(this.currentImminentRiskAlert?.id, ImminentRiskStates.InProgress, audioHeard);
    }

}
